import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, StatusBadge, formatCurrency, formatDate } from "@/components/shared";
import { ArrowLeft, Save, Trash2, Plus, X, FileDown, Phone, Send } from "lucide-react";
import { toast } from "sonner";

const FIELD = "text-xs uppercase tracking-wider text-[#737A76]";

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <Label className={FIELD}>{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="bg-white border-[#EAEAE5]" />
    </div>
  );
}

export default function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [utilityHistory, setUtilityHistory] = useState(null);
  const [utilityMonths, setUtilityMonths] = useState(6);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, r, i, u] = await Promise.all([
          api.get(`/tenants/${id}`),
          api.get(`/reservations?tenant_id=${id}`),
          api.get(`/invoices?tenant_id=${id}`),
          api.get(`/tenants/${id}/utility-history?months=${utilityMonths}`),
        ]);
        setTenant(t.data);
        setReservations(r.data);
        setInvoices(i.data);
        setUtilityHistory(u.data);
      } catch {
        toast.error("Failed to load tenant");
      }
    })();
  }, [id]);

  // Re-fetch only the utility grid when the months selector changes (after initial load).
  useEffect(() => {
    if (!tenant) return;
    api.get(`/tenants/${id}/utility-history?months=${utilityMonths}`)
      .then(({ data }) => setUtilityHistory(data))
      .catch(() => toast.error("Failed to refresh utility history"));
  }, [utilityMonths]);

  if (!tenant) return <div className="p-8 text-sm text-[#737A76]" data-testid="tenant-loading">Loading…</div>;

  const update = (patch) => setTenant({ ...tenant, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const { id: _, created_at, ...payload } = tenant;
      await api.put(`/tenants/${id}`, payload);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this tenant? This also deletes their reservations and invoices.")) return;
    try {
      await api.delete(`/tenants/${id}`);
      toast.success("Tenant deleted");
      navigate("/tenants");
    } catch {
      toast.error("Delete failed");
    }
  };

  const addVehicle = () => update({ vehicles: [...(tenant.vehicles || []), { type: "RV", make: "", model: "", license_plate: "", state: "", year: null }] });
  const rmVehicle = (i) => update({ vehicles: tenant.vehicles.filter((_, idx) => idx !== i) });
  const updVehicle = (i, k, v) => update({ vehicles: tenant.vehicles.map((veh, idx) => idx === i ? { ...veh, [k]: v } : veh) });

  const addPet = () => update({ pets: [...(tenant.pets || []), { type: "dog", breed: "", weight_lbs: null, name: "" }] });
  const rmPet = (i) => update({ pets: tenant.pets.filter((_, idx) => idx !== i) });
  const updPet = (i, k, v) => update({ pets: tenant.pets.map((p, idx) => idx === i ? { ...p, [k]: v } : p) });

  return (
    <div data-testid="tenant-detail-page">
      <div className="flex items-center gap-2 mb-3">
        <Button variant="ghost" onClick={() => navigate("/tenants")} className="-ml-2" data-testid="back-button">
          <ArrowLeft size={14} className="mr-1" /> Tenants
        </Button>
      </div>
      <PageHeader
        title={`${tenant.first_name} ${tenant.last_name}`}
        subtitle={tenant.email || tenant.cell_phone || "Tenant profile"}
        actions={
          <>
            <Button variant="outline" className="border-[#EAEAE5]" onClick={async () => {
              try {
                const { data } = await api.post(`/integrations/ghl/tenants/${id}/sync`);
                toast.success(`Synced to FreedomCRM (contact ${data.ghl_contact_id || "—"})`);
                const t = await api.get(`/tenants/${id}`); setTenant(t.data);
              } catch (e) { toast.error(e.response?.data?.detail || "CRM sync failed"); }
            }} data-testid="push-tenant-ghl-button">
              <Send size={14} className="mr-2" /> Push to CRM
            </Button>
            <Button variant="outline" className="border-[#EAEAE5]" onClick={() => window.open(`${API}/tenants/${id}/statement.pdf`, "_blank")} data-testid="download-statement-button">
              <FileDown size={14} className="mr-2" /> Statement PDF
            </Button>
            <Button variant="outline" className="border-[#A13E34] text-[#A13E34] hover:bg-[#F5E3E0]" onClick={remove} data-testid="delete-tenant-button">
              <Trash2 size={14} className="mr-2" /> Delete
            </Button>
            <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={save} disabled={saving} data-testid="save-tenant-button">
              <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save"}
            </Button>
          </>
        }
      />

      <Tabs defaultValue="contact" className="w-full">
        <TabsList className="bg-[#F0F0EC] h-auto p-1">
          <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
          <TabsTrigger value="employer" data-testid="tab-employer">Employer & Emergency</TabsTrigger>
          <TabsTrigger value="reservations" data-testid="tab-reservations">Reservations</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="utilities" data-testid="tab-utilities">Utilities</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles & Pets</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance">Insurance</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="contact" className="mt-4">
          <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <Field label="Salutation" value={tenant.salutation} onChange={(v) => update({ salutation: v })} />
              <Field label="First Name" value={tenant.first_name} onChange={(v) => update({ first_name: v })} testId="edit-first-name" />
              <Field label="MI" value={tenant.middle_initial} onChange={(v) => update({ middle_initial: v })} />
              <Field label="Last Name" value={tenant.last_name} onChange={(v) => update({ last_name: v })} testId="edit-last-name" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Email" value={tenant.email} onChange={(v) => update({ email: v })} />
              <Field label="Cell Phone" value={tenant.cell_phone} onChange={(v) => update({ cell_phone: v })} />
              <Field label="Home Phone" value={tenant.home_phone} onChange={(v) => update({ home_phone: v })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date of Birth" value={tenant.date_of_birth} onChange={(v) => update({ date_of_birth: v })} />
              <Field label="Nickname" value={tenant.nickname} onChange={(v) => update({ nickname: v })} />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mt-2">Address</div>
            <Field label="Street" value={tenant.address} onChange={(v) => update({ address: v })} />
            <div className="grid grid-cols-4 gap-4">
              <Field label="City" value={tenant.city} onChange={(v) => update({ city: v })} />
              <Field label="State" value={tenant.state} onChange={(v) => update({ state: v })} />
              <Field label="Zip" value={tenant.zip_code} onChange={(v) => update({ zip_code: v })} />
              <Field label="Country" value={tenant.country} onChange={(v) => update({ country: v })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Access Card" value={tenant.access_card} onChange={(v) => update({ access_card: v })} />
              <Field label="Transmitter" value={tenant.transmitter} onChange={(v) => update({ transmitter: v })} />
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="employer" className="mt-4">
          <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-4">
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Employer</div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Employer" value={tenant.employer_name} onChange={(v) => update({ employer_name: v })} />
              <Field label="Phone" value={tenant.employer_phone} onChange={(v) => update({ employer_phone: v })} />
              <Field label="Email" value={tenant.employer_email} onChange={(v) => update({ employer_email: v })} />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mt-4">Emergency Contact</div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Name" value={tenant.emergency_name} onChange={(v) => update({ emergency_name: v })} />
              <Field label="Phone" value={tenant.emergency_phone} onChange={(v) => update({ emergency_phone: v })} />
              <Field label="Relationship" value={tenant.emergency_relation} onChange={(v) => update({ emergency_relation: v })} />
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="reservations" className="mt-4">
          <Card className="border-[#EAEAE5] shadow-none overflow-hidden"><CardContent className="p-0">
            <table className="w-full text-sm pd-table">
              <thead><tr className="text-left border-b border-[#EAEAE5] bg-[#FAFAF7]">
                <th className="px-4 py-3">Confirmation</th><th>Type</th><th>Check In</th><th>Check Out</th><th>Total</th><th>Status</th>
              </tr></thead>
              <tbody>
                {reservations.length === 0 ? (
                  <tr><td className="px-4 py-8 text-center text-[#737A76]" colSpan={6}>No reservations.</td></tr>
                ) : reservations.map((r) => (
                  <tr key={r.id} className="border-b border-[#F0F0EC] last:border-0 cursor-pointer" onClick={() => navigate(`/reservations/${r.id}`)}>
                    <td className="px-4 py-3 font-medium">{r.confirmation_no}</td>
                    <td>{r.lease_type}</td>
                    <td>{formatDate(r.check_in)}</td>
                    <td>{formatDate(r.check_out)}</td>
                    <td>{formatCurrency(r.total)}</td>
                    <td><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="billing" className="mt-4">
          <Card className="border-[#EAEAE5] shadow-none overflow-hidden"><CardContent className="p-0">
            <table className="w-full text-sm pd-table">
              <thead><tr className="text-left border-b border-[#EAEAE5] bg-[#FAFAF7]">
                <th className="px-4 py-3">Invoice</th><th>Date</th><th>Description</th><th>Total</th><th>Balance</th><th>Status</th>
              </tr></thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td className="px-4 py-8 text-center text-[#737A76]" colSpan={6}>No invoices.</td></tr>
                ) : invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-[#F0F0EC] last:border-0 cursor-pointer" onClick={() => navigate(`/invoices/${inv.id}`)}>
                    <td className="px-4 py-3 font-medium">{inv.invoice_no}</td>
                    <td>{formatDate(inv.date)}</td>
                    <td className="text-[#737A76]">{inv.description}</td>
                    <td>{formatCurrency(inv.total)}</td>
                    <td>{formatCurrency(inv.balance)}</td>
                    <td><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="utilities" className="mt-4">
          <Card className="border-[#EAEAE5] shadow-none overflow-hidden" data-testid="utility-history-card">
            <CardContent className="p-0">
              {!utilityHistory ? (
                <div className="px-4 py-8 text-center text-[#737A76] text-sm">Loading utility history…</div>
              ) : (
                <>
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#EAEAE5] bg-[#FAFAF7] flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <Label className="text-xs uppercase tracking-wider text-[#737A76] m-0">Range</Label>
                      <select
                        value={utilityMonths}
                        onChange={(e) => setUtilityMonths(parseInt(e.target.value, 10))}
                        className="h-8 px-2 text-sm border border-[#EAEAE5] rounded-md bg-white"
                        data-testid="utility-history-months-select"
                      >
                        <option value={3}>3 months</option>
                        <option value={6}>6 months</option>
                        <option value={12}>12 months</option>
                        <option value={24}>24 months</option>
                      </select>
                      <div className="text-xs text-[#737A76]">
                        Grand total <span className="font-semibold text-[#284B3B]">{formatCurrency(utilityHistory.grand_total)}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#EAEAE5]"
                      disabled={utilityHistory.grand_total === 0}
                      onClick={() => window.open(`${API}/tenants/${id}/utility-history.pdf?months=${utilityMonths}`, "_blank")}
                      data-testid="utility-history-pdf-button"
                    >
                      <FileDown size={14} className="mr-2" /> Export PDF
                    </Button>
                  </div>
                  {utilityHistory.grand_total === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-[#737A76]" data-testid="utility-history-empty">
                      No utility charges in the last {utilityHistory.months.length} months.
                      <div className="text-xs mt-1 text-[#9BA29D]">
                        Utilities are billed automatically on stays of 30 days or longer when the park has them
                        configured as billed (Settings → Utilities &amp; Pricing).
                      </div>
                    </div>
                  ) : (
                  <table className="w-full text-sm pd-table" data-testid="utility-history-table">
                  <thead>
                    <tr className="text-left border-b border-[#EAEAE5] bg-[#FAFAF7]">
                      <th className="px-4 py-3">Month</th>
                      {utilityHistory.utilities.map((u) => (
                        <th key={u} className="text-right pr-4">{u}</th>
                      ))}
                      <th className="text-right pr-4">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {utilityHistory.rows.map((row) => (
                      <tr key={row.month} className="border-b border-[#F0F0EC] last:border-0" data-testid={`utility-row-${row.month}`}>
                        <td className="px-4 py-3 font-medium">{row.month}</td>
                        {utilityHistory.utilities.map((u) => (
                          <td key={u} className="text-right pr-4 text-[#737A76]">
                            {row[u] > 0 ? formatCurrency(row[u]) : "—"}
                          </td>
                        ))}
                        <td className="text-right pr-4 font-medium">
                          {row.total > 0 ? formatCurrency(row.total) : "—"}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-[#FAFAF7] font-semibold" data-testid="utility-history-totals">
                      <td className="px-4 py-3">Total</td>
                      {utilityHistory.utilities.map((u) => (
                        <td key={u} className="text-right pr-4">
                          {utilityHistory.totals[u] > 0 ? formatCurrency(utilityHistory.totals[u]) : "—"}
                        </td>
                      ))}
                      <td className="text-right pr-4">{formatCurrency(utilityHistory.grand_total)}</td>
                    </tr>
                  </tbody>
                </table>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vehicles" className="mt-4">
          <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Vehicles</div>
              <Button size="sm" variant="outline" onClick={addVehicle} data-testid="add-vehicle-button"><Plus size={12} className="mr-1" /> Add</Button>
            </div>
            {(tenant.vehicles || []).map((v, i) => (
              <div key={i} className="grid grid-cols-6 gap-2 items-end">
                <Field label="Type" value={v.type} onChange={(val) => updVehicle(i, "type", val)} />
                <Field label="Year" value={v.year || ""} onChange={(val) => updVehicle(i, "year", parseInt(val) || null)} />
                <Field label="Make" value={v.make} onChange={(val) => updVehicle(i, "make", val)} />
                <Field label="Model" value={v.model} onChange={(val) => updVehicle(i, "model", val)} />
                <Field label="Plate" value={v.license_plate} onChange={(val) => updVehicle(i, "license_plate", val)} />
                <Button variant="ghost" size="icon" onClick={() => rmVehicle(i)} className="mb-0.5"><X size={14} /></Button>
              </div>
            ))}

            <div className="flex items-center justify-between mt-6">
              <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Pets</div>
              <Button size="sm" variant="outline" onClick={addPet} data-testid="add-pet-button"><Plus size={12} className="mr-1" /> Add</Button>
            </div>
            {(tenant.pets || []).map((p, i) => (
              <div key={i} className="grid grid-cols-6 gap-2 items-end">
                <Field label="Type" value={p.type} onChange={(val) => updPet(i, "type", val)} />
                <Field label="Name" value={p.name} onChange={(val) => updPet(i, "name", val)} />
                <Field label="Breed" value={p.breed} onChange={(val) => updPet(i, "breed", val)} />
                <Field label="Weight (lbs)" value={p.weight_lbs || ""} onChange={(val) => updPet(i, "weight_lbs", parseFloat(val) || null)} />
                <div />
                <Button variant="ghost" size="icon" onClick={() => rmPet(i)} className="mb-0.5"><X size={14} /></Button>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="insurance" className="mt-4">
          <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Insurance Company" value={tenant.insurance_company} onChange={(v) => update({ insurance_company: v })} />
              <Field label="Policy No." value={tenant.insurance_policy} onChange={(v) => update({ insurance_policy: v })} />
              <Field label="Agent" value={tenant.insurance_agent} onChange={(v) => update({ insurance_agent: v })} />
            </div>
            <Field label="Lien Holder" value={tenant.lien_holder} onChange={(v) => update({ lien_holder: v })} />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="notes" className="mt-4">
          <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6">
            <Label className={FIELD}>Special instructions / notes</Label>
            <Textarea value={tenant.notes || ""} onChange={(e) => update({ notes: e.target.value })} className="mt-2 min-h-[180px] bg-white border-[#EAEAE5]" data-testid="tenant-notes-input" />
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
